import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Copy, Loader2, Landmark, ScanLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { OrderBreakdown } from "@/components/OrderBreakdown";
import { api, errMsg, imgUrl, rupiah, tableLabel } from "@/lib/api";

const HighlightAmount = ({ value, code }) => {
  const txt = rupiah(value);
  if (!code) return txt;
  return <>{txt.slice(0, -3)}<span className="text-amber-700 bg-amber-100 rounded px-0.5">{txt.slice(-3)}</span></>;
};

const UniqueBox = ({ order }) => (
  <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm" data-testid="payment-unique-code">
    <div className="font-semibold text-amber-900">Bayar tepat sampai 3 digit terakhir</div>
    <div className="text-amber-900/80 mt-1">Pesanan {rupiah(order.total - order.unique_code)} + kode unik <b>{order.unique_code}</b>. Kode unik membantu staf mengenali pembayaranmu lebih cepat.</div>
  </div>
);

const BankBox = ({ s, total }) => {
  const copy = (txt, label) => navigator.clipboard?.writeText(String(txt)).then(() => toast.success(`${label} disalin`));
  return (
    <div className="bg-paper rounded-xl p-5 space-y-4" data-testid="bank-transfer-details">
      <div className="flex items-center gap-3"><Landmark className="w-6 h-6" strokeWidth={1.6} /><span className="font-semibold">{s.bank_name}</span></div>
      <div>
        <div className="text-xs text-muted2">Nomor rekening</div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-2xl font-bold tracking-wider" data-testid="bank-account-number">{s.account_number}</span>
          <button onClick={() => copy(s.account_number, "Nomor rekening")} className="p-2 rounded-lg border border-sage-200 hover:bg-sage-100" data-testid="copy-account-button"><Copy className="w-4 h-4" /></button>
        </div>
        <div className="text-sm text-ink/80">a.n. {s.account_holder}</div>
      </div>
      <button onClick={() => copy(total, "Nominal")} className="text-sm text-forest font-semibold underline underline-offset-4" data-testid="copy-amount-button">Salin nominal {rupiah(total)}</button>
    </div>
  );
};

const QrisBox = ({ s }) => (
  <div className="bg-paper rounded-xl p-5 text-center" data-testid="qris-details">
    <div className="flex items-center justify-center gap-2 font-semibold"><ScanLine className="w-5 h-5" /> QRIS · {s.qris_merchant}</div>
    <img src={imgUrl(s.qris_image)} alt="Kode QRIS" className="w-60 h-60 object-contain mx-auto mt-4 rounded-lg bg-white p-2 border border-sage-200" data-testid="qris-image" />
    <p className="text-xs text-muted2 mt-3">Scan dengan m-banking atau e-wallet (GoPay, OVO, DANA, ShopeePay, dll).</p>
  </div>
);

export default function PaymentPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const statusUrl = `/pesanan/${id}?t=${token}`;

  useEffect(() => {
    api.get(`/orders/${id}`, { params: { token } })
      .then(async (o) => {
        const s = await api.get(`/outlets/${o.data.outlet_code}`);
        return [o, s];
      })
      .then(([o, s]) => {
        if (o.data.status !== "menunggu_pembayaran") navigate(statusUrl, { replace: true });
        setOrder(o.data);
        setSettings(s.data);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [id, token, navigate, statusUrl]);

  const paid = async () => {
    setBusy(true);
    try {
      await api.post(`/orders/${id}/paid`, null, { params: { token } });
      navigate(statusUrl);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!order || !settings) return (<div className="min-h-screen bg-cream"><Header /><div className="grid place-items-center py-32"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div></div>);

  return (
    <div className="min-h-screen bg-cream">
      <Header />
      <main className="max-w-lg mx-auto px-4 py-10 rise" data-testid="payment-page">
        <span className="text-xs font-semibold tracking-[0.15em] text-forest">LANGKAH 2 DARI 3</span>
        <h1 className="font-serif font-semibold text-4xl mt-2">Selesaikan pembayaran</h1>
        <p className="text-muted2 mt-2">Pesanan <b className="text-ink" data-testid="payment-order-code">{order.code}</b> · Gerai {order.outlet_name} · {tableLabel(order.floor, order.table)}</p>
        <div className="bg-sage-100 border border-sage-200 rounded-2xl p-5 mt-6">
          <div className="flex justify-between items-end">
            <span className="text-sm text-muted2">Total bayar</span>
            <span className="text-3xl font-bold" data-testid="payment-total"><HighlightAmount value={order.total} code={order.unique_code} /></span>
          </div>
          {order.unique_code > 0 && <UniqueBox order={order} />}
          <div className="mt-5">{order.payment_method === "qris" ? <QrisBox s={settings} /> : <BankBox s={settings} total={order.total} />}</div>
          <ul className="text-sm text-ink/80 mt-5 space-y-1">
            {order.items.map((i) => <li key={i.menu_id}><div className="flex justify-between"><span>{i.qty}× {i.name}</span><span>{rupiah(i.qty * i.price)}</span></div>{i.note && <div className="text-xs text-muted2">Catatan: {i.note}</div>}</li>)}
          </ul>
          {order.notes && <p className="text-xs text-muted2 mt-2">Catatan: {order.notes}</p>}
          {order.coins_used > 0 && <p className="text-sm text-forest mt-2" data-testid="payment-coin-discount">Sudah dipotong {order.coins_used} Coin (-{rupiah(order.discount)}) dari {rupiah(order.subtotal)}</p>}
          <div className="mt-4 pt-3 border-t border-sage-200"><OrderBreakdown o={order} testid="payment-breakdown" /></div>
        </div>
        <button onClick={paid} disabled={busy} className="btn-forest w-full py-4 text-lg mt-6 flex items-center justify-center gap-2" data-testid="confirm-payment-done-button">
          {busy && <Loader2 className="w-5 h-5 animate-spin" />} Saya Sudah Bayar
        </button>
        <p className="flex items-center justify-center gap-2 text-xs text-muted2 mt-3"><ShieldCheck className="w-4 h-4" /> Staf akan mengecek dan mengonfirmasi pembayaranmu.</p>
      </main>
    </div>
  );
}
